import { useTranslation } from "react-i18next"

import type { BrandingAssetRef } from "@/api/admin/branding"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { BRANDING_ASSET_SLOTS } from "./branding-config-constants"

type BrandingAssetGridProps = {
  assets: Record<string, BrandingAssetRef | null>
  deleting: boolean
  onDelete: (assetKey: string) => void
  onUpload: (assetKey: string, file: File | undefined) => void
}

export function BrandingAssetGrid({
  assets,
  deleting,
  onDelete,
  onUpload,
}: BrandingAssetGridProps) {
  const { t } = useTranslation()

  return (
    <section className="grid gap-3">
      <h3 className="text-sm font-semibold">
        {t("adminDashboard.branding.assetsTitle", { defaultValue: "Logos and icons" })}
      </h3>
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.branding.assetsHelp", {
          defaultValue: "Uploads apply immediately. Logos may be SVG; PWA icons must be PNG.",
        })}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {BRANDING_ASSET_SLOTS.map((slot) => {
          const ref = assets[slot.key]
          return (
            <div key={slot.key} className="grid gap-2 rounded-md border p-3">
              <Label htmlFor={`asset-${slot.key}`}>
                {t(slot.labelKey, { defaultValue: slot.labelFallback })}
              </Label>
              {ref ? (
                <img
                  alt=""
                  className="bg-muted h-12 w-auto max-w-full object-contain"
                  src={ref.versionedUrl}
                />
              ) : (
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.branding.assetEmpty", { defaultValue: "None uploaded" })}
                </p>
              )}
              <Input
                accept={slot.accept}
                id={`asset-${slot.key}`}
                type="file"
                onChange={(event) => {
                  onUpload(slot.key, event.target.files?.[0])
                  event.target.value = ""
                }}
              />
              {ref ? (
                <Button
                  disabled={deleting}
                  size="sm"
                  type="button"
                  variant="ghost"
                  onClick={() => onDelete(slot.key)}
                >
                  {t("adminDashboard.branding.removeAsset", { defaultValue: "Remove" })}
                </Button>
              ) : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}
